import {
    Button,
    copyToClipboard,
    DataTable,
    DialogHandler,
    EmptyContent,
    EPageBranding,
    fetchRoute,
    FullBar,
    GenericWrapperContext,
    Header,
    Icon,
    ListPageModel,
    LoadingContainer,
    PagesFactory,
    Search,
    TDataTableColumnsData,
    TDataTableRowData,
    TGenericListReturn,
    TOrder,
    TPromisePoolPartialList,
    TPromisePoolRef,
    TPromisePoolReturnData,
    UserModel,
    UsersFactory
} from "@cakemail-org/ui-components-v2"
import { Stack } from "@mui/material"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { getBasePage } from "../../../components/beeEditor/baseContent/basePage"
import { showCreateResourceDialog } from "../../../components/dialogs/createResource"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { showRenameDialog } from "../../../components/dialogs/rename"
import { listsPromisePool } from "../../../utils/promisesPools"
import { ERoutes } from "../../../utils/url"
import { TPagesListing } from "../types"
import { getListingColsData, getListingRows } from "../utils"

export default function PagesListing({ }: TPagesListing) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()

    const [openedSearch, setOpenedSearch] = useState(false)
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(25)
    const [order, setOrder] = useState<TOrder>("desc")
    const [orderBy, setOrderBy] = useState<string>("updated_on")
    const [pages, setPages] = useState<TGenericListReturn<ListPageModel>>()
    const [loading, setLoading] = useState(false)

    const [users, setUsers] = useState<{ [id: string]: UserModel }>()

    const [partialLists, setPartialLists] = useState<TPromisePoolReturnData<TPromisePoolPartialList>>({})
    const partialListsPromiseRef = useRef<TPromisePoolRef>(undefined)

    useEffect(() => {
        UsersFactory.list({
            page: 1,
            perPage: 1000
        }).then((res) => {
            const curUsers: { [k: string]: UserModel } = {}
            res.data.forEach((user: UserModel) => {
                curUsers[user.id] = user
            })
            setUsers(curUsers)
        })
    }, [])

    useEffect(handleSearchFocus, [openedSearch])

    useEffect(() => {
        fetchPages()
    }, [page, perPage, order, orderBy, search])

    useEffect(() => {
        return stopPromisePools
    }, [])

    function stopPromisePools() {
        try {
            partialListsPromiseRef.current?.stop()
            partialListsPromiseRef.current = undefined
        } catch (e) {
            // pass
        }
    }

    function fetchPages() {
        setLoading(true)
        PagesFactory.list({
            page,
            perPage,
            name: search || undefined,
            sort: order === "asc" ? orderBy : `-${orderBy}`,
            withCount: true
        }).then((data) => {
            const listIds = [...new Set(data.data.map(page => page.list_id))].map(id => ({ id }))
            listsPromisePool({ data: listIds, setter: setPartialLists, poolRef: partialListsPromiseRef })

            setPages(data)
        }).catch(() => {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("general-error-action")
            })
        }).finally(() => {
            setLoading(false)
        })
    }

    function handleCreatePage() {
        showCreateResourceDialog({
            titleKey: "general-create-page",
            addList: true
        }).then(({ btnRef, list, name }) => {
            btnRef.animate()
            return PagesFactory.create({
                name,
                list_id: list?.id,
                branding: EPageBranding.basic,
                content: {
                    json: getBasePage()
                }
            })
        }).then((newPage) => {
            DialogHandler.hide()
            window.location.href = "#" + fetchRoute({ route: ERoutes.pageDetails, params: { pageId: newPage.id } })
        }).catch((e) => {
            if (e) {
                DialogHandler.show({
                    isExpress: true,
                    type: "error",
                    title: t("general-error-action"),
                })
            }
        })
    }

    function handleSearchFocus() {
        if (!openedSearch) return
        const el = document.querySelector(".pagesSearch input") as HTMLInputElement
        if (el) el.focus()
    }

    function handleSearchChange(cSearch: string) {
        setPage(1)
        setSearch(cSearch)
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as string)
    }

    function handlePaginationChange(cPage: number) {
        setPage(cPage)
    }

    function handleSearchOpen() {
        setSearch("")
        setOpenedSearch(!openedSearch)
    }

    function handleOpenPage(page: ListPageModel) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.pageDetails, params: { pageId: page.id } })
    }

    function handleDuplicatePage(page: ListPageModel) {
        PagesFactory.get({ id: page.id })
            .then((data) => {
                const newPage = data.toJson()
                newPage.name = newPage.name + ` (${t("general-copy")})`
                delete newPage.form_id
                return PagesFactory.create(newPage)
            })
            .then(() => {
                fetchPages()
            }).catch((e) => {
                DialogHandler.show({
                    isExpress: true,
                    type: "error",
                    title: t("general-error-action"),
                })
            })
    }

    function handleRenamePage(page: ListPageModel) {
        showRenameDialog({ initName: page.name || "", resourceKey: "general-page_one" })
            .then(({ value }) => {
                return page.save({ name: value })
            }).then(() => {
                fetchPages()
                DialogHandler.hide()
            }).catch((e) => {
                if (e) {
                    DialogHandler.show({
                        isExpress: true,
                        type: "error",
                        title: t("general-error-action"),
                    })
                }
            })
    }

    function handlePreviewPage(page: ListPageModel) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.pageDetails, params: { pageId: page.id } }) + "?preview=true"
    }

    function handleCopyPageUrl(page: ListPageModel) {
        copyToClipboard(page.published_url || "")
    }

    function handleDeletePage(page: ListPageModel) {
        showDeleteDialog({
            name: page.name || "",
            resourceKey: "general-page_one",
            validationWordKey: "input-validation-key-delete"
        }).then(({ btnRef }) => {
            btnRef.animate()
            return page.delete()
        }).then(() => {
            DialogHandler.show({
                isExpress: true,
                type: "success",
                title: t("page-deleted")
            })
            fetchPages()
        }).catch((e) => {
            if (e)
                DialogHandler.show({
                    isExpress: true,
                    type: "warning",
                    title: t("general-error-action")
                })
        })
    }


    const colsData: TDataTableColumnsData[] = useMemo(getListingColsData(), [])

    const rows: any[] = pages && pages.data.map((page: ListPageModel, index) => {
        const partialList: Partial<TPromisePoolPartialList> = page.list_id ? partialLists?.[page.list_id] : {}
        return getListingRows({
            partialList,
            page,
            users,
            handleOpenPage,
            handleDuplicatePage,
            handleRenamePage,
            handlePreviewPage,
            handleDeletePage,
            handleCopyPageUrl
        })
    }) || []

    return <Stack className="pagesListingContainer" gap={8}>
        <FullBar
            className={loading ? "loading" : ""}
            left={<Header
                heading={t("general-page_other")}
                headingVariant="titleL"
                support={genericWrapper.account?.name}
                headingTooltip={t("page-heading-tooltip")}
            />}
            right={[
                <Button
                    key="create-page"
                    variant="contained"
                    size="medium"
                    color="secondary"
                    onClick={handleCreatePage}
                    iconOnly
                    disabled={loading}
                ><Icon name="Add18" /></Button>,
                <Button
                    variant="outlined"
                    size="medium"
                    color="body1"
                    onClick={handleSearchOpen}
                    iconOnly
                    disabled={loading}
                ><Icon name="Search18" /></Button>
            ]}
        />

        {openedSearch &&
            <Search placeholder={t("pages-listing-search-placeholder")} initValue={search} className={`pagesSearch ${loading ? "loading" : ""}`} onChange={handleSearchChange} onClose={handleSearchOpen} useClickAway />
        }

        <LoadingContainer loading={!pages}>
            {pages && pages.data.length === 0 && !search &&
                <EmptyContent
                    title={t("pages-listing-empty-title")}
                    desc={t("pages-listing-empty-desc")}
                    iconName="CreateForm44"
                    button={<Button
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleCreatePage} iconOnly>
                        {t("general-create-page")}
                    </Button>}
                />
            }

            {pages && pages.data.length === 0 && search &&
                <EmptyContent
                    title={t("pages-listing-empty-search-title")}
                    desc={t("search-empty-desc")}
                />
            }

            {pages && pages.data.length > 0 &&
                <DataTable
                    initState={{
                        rowsPerPage: perPage,
                        order,
                        orderBy
                    }}
                    totalCount={pages?.pagination?.count}
                    className={`pagesDataTable ${loading ? "loading" : ""}`}
                    onPaginationChange={handlePaginationChange}
                    onSortChange={handleSortChange}
                    rows={rows}
                    columnsData={colsData}
                    stickyLastCol
                />
            }

        </LoadingContainer>
    </Stack>
}